import React from 'react';
import { Container, Typography, Box } from '@mui/material';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <Box sx={{ flexGrow: 1 }}>
      <nav className="bg-white shadow-lg border-b border-gray-200 fixed top-0 left-0 right-0 z-50">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            {/* Logo */}
            <div className="flex items-center">
              <div className="flex-shrink-0 flex items-center ml-2 md:ml-4">
                <div className="h-8 w-8 md:h-10 md:w-10 rounded-lg flex items-center justify-center overflow-hidden">
                  <img 
                    src="/brasao_unifap.png" 
                    alt="UNIFAP" 
                    className="h-8 w-8 md:h-10 md:w-10 object-contain"
                  />
                </div>
                <span className="ml-2 md:ml-3 text-lg md:text-xl font-semibold text-gray-900 hidden sm:block">
                  Portal de Serviços
                </span>
              </div>
            </div>
          </div>
        </div>
      </nav>
      
      <Container maxWidth="md" sx={{ bgcolor: 'background.paper', py: 4, pt: 8 }}>
        <Box py={4}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'justify' }}>
            Política de Privacidade - Aplicativo Minha UNIFAP
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4, textAlign: 'justify' }}>
            1. Introdução
          </Typography>

          <Typography paragraph sx={{ textAlign: 'justify' }}>
            A Universidade Federal do Amapá (UNIFAP), por meio de sua equipe responsável pelo desenvolvimento e manutenção do Aplicativo Minha UNIFAP, declara que o documento intitulado "Política de Privacidade do Aplicativo Minha UNIFAP" foi elaborado em conformidade com os princípios, fundamentos e diretrizes estabelecidos pela Lei nº 13.709, de 14 de agosto de 2018 — Lei Geral de Proteção de Dados Pessoais (LGPD).
          </Typography>

          <Typography paragraph sx={{ textAlign: 'justify' }}>
            A Política adota medidas de segurança técnicas e administrativas para proteção de dados pessoais, assegurando a observância dos princípios da finalidade, adequação, necessidade, transparência, segurança e responsabilização, conforme previsto na referida legislação.
          </Typography>

          <Typography paragraph sx={{ textAlign: 'justify' }}>
            Estamos comprometidos em proteger suas informações pessoais. Esta Política de Privacidade descreve como o aplicativo Minha UNIFAP, um produto tecnológico e científico desenvolvido dentro da instituição, coleta, usa e protege suas informações. Ao utilizar o aplicativo, você concorda com os termos descritos a seguir.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            Definições
          </Typography>

          <Typography paragraph sx={{ textAlign: 'justify' }}>
            Para melhor compreensão deste documento, descrevemos abaixo o conceito das expressões utilizadas:
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            Serviços disponíveis no aplicativo
          </Typography>

          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            <Typography component="li" sx={{ textAlign: 'justify', mb: 1.5 }}>
              <strong>Mapa Fácil:</strong> Mapa com navegação pelo campus com facilidade através do google maps, encontrando prédios, salas e pontos de interesse de forma rápida e eficiente.
            </Typography>
            <Typography component="li" sx={{ textAlign: 'justify', mb: 1.5 }}>
              <strong>Calendário Acadêmico:</strong> Documento oficial do calendário acadêmico atualizado com as datas importantes do ano letivo, prazos de matrícula, eventos e feriados.
            </Typography>
            <Typography component="li" sx={{ textAlign: 'justify', mb: 1.5 }}>
              <strong>Cardápio RU:</strong> Documento com a descrição das refeições disponibilizadas semanalmente no cardápio do Restaurante Universitário.
            </Typography>
            <Typography component="li" sx={{ textAlign: 'justify', mb: 1.5 }}>
              <strong>Rádio Universitária:</strong> Programação da rádio da UNIFAP, com conteúdos educativos, culturais e informativos.
            </Typography>
            <Typography component="li" sx={{ textAlign: 'justify', mb: 1.5 }}>
              <strong>Cuide da UNIFAP:</strong> É um canal direto para que a comunidade acadêmica possa comunicar à universidade sobre necessidades de manutenção ou melhorias em sua infraestrutura, fortalecendo a participação e o cuidado coletivo. Este serviço está disponível para servidores e alunos.
            </Typography>
            <Typography component="li" sx={{ textAlign: 'justify' }}>
              <strong>Carteira digital</strong> — um recurso moderno que oferece uma versão digital da identidade funcional e estudantil, trazendo praticidade e segurança para o dia a dia na universidade, disponível somente para servidor e aluno da universidade
            </Typography>
          </Box>

          <Typography variant="h5" gutterBottom sx={{ mt: 4, textAlign: 'justify' }}>
            1. Coleta de dados
          </Typography>

          <Typography paragraph sx={{ textAlign: 'justify' }}>
            O aplicativo Minha UNIFAP foi projetado para ser um recurso de fácil acesso à informação e serviços da universidade, e a coleta de dados é mínima e estritamente necessária para sua funcionalidade.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
            1.1. Dados Pessoais Coletados (Usuários Vinculados - Alunos e Servidores)
          </Typography>

          <Typography paragraph sx={{ textAlign: 'justify' }}>
            Para acessar os serviços de carteira digital e o módulo "Cuide da UNIFAP," é necessário que o usuário faça login. Nesse processo, o aplicativo pode coletar e processar os seguintes dados pessoais:
          </Typography>

          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            <Typography component="li" sx={{ textAlign: 'justify', mb: 1 }}>
              Nome
            </Typography>
            <Typography component="li" sx={{ textAlign: 'justify', mb: 1 }}>
              Cargo
            </Typography>
            <Typography component="li" sx={{ textAlign: 'justify', mb: 1 }}>
              Matrícula ou identificação funcional
            </Typography>
            <Typography component="li" sx={{ textAlign: 'justify' }}>
              CPF
            </Typography>
          </Box>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            1.2. Dados Coletados da Comunidade em Geral (Uso Anônimo)
          </Typography>

          <Typography paragraph sx={{ textAlign: 'justify' }}>
            Para o uso de funcionalidades como o Mapa Fácil, Calendário Acadêmico, Cardápio do Restaurante Universitário e Rádio Universitária, o aplicativo não coleta nenhuma informação pessoal de identificação. Seu uso é totalmente anônimo.
          </Typography>

          <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
            1.3. Dados Sensíveis e Permissões
          </Typography>

          <Typography paragraph sx={{ textAlign: 'justify' }}>
            O aplicativo Minha UNIFAP não coleta dados sensíveis como localização, dados de saúde, microfone, câmera, SMS ou registros de chamadas.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4, textAlign: 'justify' }}>
            2. Uso dos dados
          </Typography>

          <Typography paragraph sx={{ textAlign: 'justify' }}>
            Os dados pessoais coletados (Nome, Matrícula/Identificação funcional, CPF ) são utilizados exclusivamente para os seguintes propósitos:
          </Typography>

          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            <Typography component="li" sx={{ textAlign: 'justify', mb: 1 }}>
              Autenticar a identidade do usuário vinculado (Aluno ou Servidor).
            </Typography>
            <Typography component="li" sx={{ textAlign: 'justify', mb: 1 }}>
              Permitir o acesso seguro aos serviços de carteira digital e ao módulo "Cuide da UNIFAP".
            </Typography>
            <Typography component="li" sx={{ textAlign: 'justify' }}>
              Prestação dos serviços a que se destinam as funcionalidades de carteira digital e do módulo "Cuide da UNIFAP".
            </Typography>
          </Box>

          <Typography paragraph sx={{ textAlign: 'justify', mt: 2 }}>
            O aplicativo Minha UNIFAP não utiliza os dados coletados para fins de anúncios ou publicidade.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4, textAlign: 'justify' }}>
            3. Compartilhamento de dados
          </Typography>

          <Typography paragraph sx={{ textAlign: 'justify' }}>
            A UNIFAP não compartilha suas informações pessoais com terceiros, exceto nos seguintes casos:
          </Typography>

          <Box component="ul" sx={{ pl: 3, m: 0 }}>
            <Typography component="li" sx={{ textAlign: 'justify', mb: 1 }}>
              Quando exigido por lei.
            </Typography>
            <Typography component="li" sx={{ textAlign: 'justify' }}>
              Com o seu consentimento explícito.
            </Typography>
          </Box>

          <Typography paragraph sx={{ textAlign: 'justify', mt: 2 }}>
            Os dados coletados são tratados internamente, dentro dos sistemas seguros da universidade.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4, textAlign: 'justify' }}>
            4. Segurança dos dados
          </Typography>

          <Typography paragraph sx={{ textAlign: 'justify' }}>
            A segurança de seus dados é fundamental. A UNIFAP emprega medidas técnicas e administrativas para proteger as informações coletadas pelo aplicativo Minha UNIFAP contra acesso não autorizado, uso indevido, alteração ou destruição. Seus dados são armazenados em servidores seguros, com acesso restrito a pessoal autorizado.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4, textAlign: 'justify' }}>
            5. Exclusão de dados
          </Typography>

          <Typography paragraph sx={{ textAlign: 'justify' }}>
            Você, como titular dos dados, tem o direito de solicitar informações sobre quais dados são tratados e a finalidade de seu uso. Para quaisquer dúvidas ou solicitações relacionadas a esta política, aos seus dados, ou para solicitar a exclusão de seus dados, você pode entrar em contato com a equipe de suporte do aplicativo Minha UNIFAP através dos canais oficiais da UNIFAP.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4, textAlign: 'justify' }}>
            6. Consentimento
          </Typography>

          <Typography paragraph sx={{ textAlign: 'justify' }}>
            O aplicativo Minha UNIFAP não lida com dados pessoais sensíveis que exijam um processo de consentimento específico sob a lei. Ao utilizar o aplicativo e fazer login, o usuário concorda com os termos descritos nesta política.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4, textAlign: 'justify' }}>
            7. Público-alvo e conformidade
          </Typography>

          <Typography paragraph sx={{ textAlign: 'justify' }}>
            O público-alvo principal do aplicativo são os membros da comunidade acadêmica da UNIFAP (Alunos e Servidores). A UNIFAP se compromete a cumprir todas as leis e regulamentos aplicáveis.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4, textAlign: 'justify' }}>
            8. Alterações nesta Política de Privacidade
          </Typography>

          <Typography paragraph sx={{ textAlign: 'justify' }}>
            Esta Política de Privacidade pode ser atualizada periodicamente para refletir novas funcionalidades do aplicativo ou alterações nas leis e regulamentos. Recomendamos que você a revise regularmente para se manter informado. A data da última atualização estará sempre disponível no final desta política.
          </Typography>

          <Typography variant="h5" gutterBottom sx={{ mt: 4, textAlign: 'justify' }}>
            9. Contato
          </Typography>

          <Typography paragraph sx={{ textAlign: 'justify' }}>
            Para quaisquer dúvidas ou solicitações relacionadas a esta política ou aos seus dados, você pode entrar em contato com a equipe de suporte do aplicativo Minha UNIFAP através dos canais oficiais da UNIFAP.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicyPage; 